import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useUser, useSignIn } from '@clerk/react';
import { useAuth } from '../contexts/AuthProvider';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import BoarMark from '../components/BoarMark';
import PetanqueLoader from '../components/PetanqueLoader';

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" style={{ marginRight: 10 }}>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const Login = () => {
  const navigate = useNavigate();
  const { isSignedIn, isLoaded } = useUser();
  const { signIn } = useSignIn();
  const { user: appUser, loading: authLoading } = useAuth();
  const [error, setError] = useState('');
  const [oauthLoading, setOauthLoading] = useState(false);

  const handleGoogleSignIn = async () => {
    setOauthLoading(true);
    setError('');
    try {
      // Clerk v6 returns errors instead of throwing them
      const { error: ssoError } = await signIn.sso({
        strategy: 'oauth_google',
        redirectCallbackUrl: '/sso-callback',
        redirectUrl: '/suppliers',
      });
      if (ssoError) throw ssoError;
    } catch (err) {
      console.error('OAuth error:', err);
      setError('Sign in failed. Please try again.');
      setOauthLoading(false);
    }
  };

  useEffect(() => {
    if (appUser) navigate('/suppliers', { replace: true });
  }, [appUser, navigate]);

  if (authLoading || !isLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <PetanqueLoader size={44} roll />
      </div>
    );
  }

  if (appUser) return <Navigate to="/suppliers" replace />;

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-4">
      <Card className="w-full max-w-md border border-border bg-card shadow-pop animate-fade-up">
        <CardHeader className="space-y-3 text-center">
          <div className="flex flex-col items-center gap-3">
            <BoarMark size={64} className="shadow-glow rounded-2xl" rounded={18} />
            <div>
              <div className="text-2xl font-semibold tracking-tight">Sangly</div>
              <div className="text-[13px] text-muted-foreground">LampTwist finance hub</div>
            </div>
          </div>
          <CardTitle className="text-lg">Welcome back</CardTitle>
          <p className="text-sm text-muted-foreground">Sign in to track supplier payments</p>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}
          <Button className="w-full" variant="secondary" onClick={handleGoogleSignIn} disabled={oauthLoading}>
            {oauthLoading ? (
              <PetanqueLoader size={20} className="mr-2" />
            ) : (
              <GoogleIcon />
            )}
            Continue with Google
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
